import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../components/page-header/page-header";
import GenericPopover from "../../../components/pop-over";
import useToast from "../../../hooks/useToast";
import Utils from "../../../utils";
import { TConfigSpare } from "../../../types/config";
import { TFilterModel } from "../../../types/config";
import { ePrivileges, eResultCode } from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  dateOnlyFormat,
  debounceTime,
  defaultFilterParams,
} from "../../../utils/constants";
import {
  Button,
  Drawer,
  Input,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { TConfigGroup } from "../../../types/config";
import CustomDrawer from "../../../components/drawer";
import { useNavigate } from "react-router-dom";
import utils from "../../../utils";
import ActionCard from "../../../components/actionCard/actionCard";
import { UserContext } from "../../../context";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../components/configurable-columns";
import ConfigSpareForm from "./form";
import { AddEditSpare, GetSpareList } from "../../../utils/api.constant";
import DateUtils from "../../../utils/date";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import CustomModal from "../../../components/modal";
import SpareProductForm from "./product-form";

/**
 * ServicePartnerList Component
 * This component renders the list of service partners, with
 * the ability to search, filter, and add new service partners.
 * It also includes a bulk upload feature for importing
 * service partners in bulk.
 *
 * @returns {JSX.Element} The JSX structure for the
 *         ServicePartnerList component.
 */

export default function ConfigGroupList1() {
  // State and hooks initialization
  const navigate = useNavigate();
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TConfigSpare[]>([]);
  const { onShowToast } = useToast();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedSpare, setSelectedSpare] = useState<TConfigSpare | null>(null);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  const initialColumns: any[] = [
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
      width: "30%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "productName"),
      sorter: (a: TConfigSpare, b: TConfigSpare) =>
        a.productName.localeCompare(b.productName),
      onFilter: (value: string | number | boolean, record: TConfigSpare) =>
        record.productName?.includes(value as string),
    },
    {
      title: "Part Number",
      dataIndex: "partNumber",
      key: "partNumber",
      width: "15%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "partNumber"),
      sorter: (a: TConfigSpare, b: TConfigSpare) =>
        a.partNumber.localeCompare(b.partNumber),
      onFilter: (value: string | number | boolean, record: TConfigSpare) =>
        record.partNumber?.includes(value as string),
    },
    {
      title: "Description",
      dataIndex: "partDescription",
      key: "partDescription",
      width: "30%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "partDescription"),
      sorter: (a: TConfigSpare, b: TConfigSpare) =>
        a.partDescription.localeCompare(b.partDescription),
      onFilter: (value: string | number | boolean, record: TConfigSpare) =>
        record.partDescription?.includes(value as string),
    },
    // {
    //   title: "Log Date",
    //   dataIndex: "createdOn",
    //   key: "createdOn",
    //   width: "15%",
    //   filterSearch: true,
    //   sorter: (a: TConfigSpare, b: TConfigSpare) =>
    //     String(a.createdOn).localeCompare(String(b.createdOn)),
    //   render: (createdOn: string) => {
    //     return DateUtils.format(createdOn, dateOnlyFormat);
    //   },
    // },
    {
      title: "Action",
      key: "actions",
      align: "center",
      width: "6%",
      render: (item: TConfigSpare) => (
        <>
          <GenericPopover
            placement="left"
            content={
              <div className="standardActionBox">
                {/* Edit Row with CustomDrawer trigger */}
                {Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.EDITCONFIGSPARE
                ) ? (
                  <CustomDrawer
                    size="default"
                    trigger={
                      <ActionCard text="Edit">
                        <EditOutlined />
                      </ActionCard>
                    }
                    component={ConfigSpareForm} // Passing ServicePartner component
                    componentProps={{
                      id: item.id,
                      onRefreshList: () => {
                        fetchData();
                      },
                    }}
                    title="Config Spare Form"
                  />
                ) : null}

                {/* TODO ::: Add privilege check on delete action */}
                <ActionCard
                  text="Delete"
                  onClick={() => {
                    setIsModalVisible(true);
                    setSelectedSpare(item);
                  }}
                >
                  <DeleteOutlined />
                </ActionCard>
              </div>
            }
            icon={
              <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
            }
          />
        </>
      ),
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);

  const context: any = useContext(UserContext);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetSpareList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TConfigSpare, index: number) => ({
          key: (item.id ?? "").toString(),
          srNo: index + 1,
          productName: item.productName,
          partNumber: item.partNumber,
          partDescription: item.partDescription,
          createdOn: item.createdOn,
          id: item.id,
        }));
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);
        updateColumnFilters(formattedData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const updateColumnFilters = (dataset: TConfigSpare[]) => {
    const productNameFilters = utils.getUniqueFilters(dataset, "productName");
    const partNumberFilters = utils.getUniqueFilters(dataset, "partNumber");
    const partDescriptionFilters = utils.getUniqueFilters(dataset, "partDescription");

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "productName") return { ...col, filters: productNameFilters };
        if (col.key === "partNumber") return { ...col, filters: partNumberFilters };
        if (col.key === "partDescription") return { ...col, filters: partDescriptionFilters };
        return col;
      })
    );
  };
  
  const filteredData = data.filter((item) => {
    const productNameMatch =
      !activeFilters.productName ||
      activeFilters.productName.includes(item.productName);
    const partNumberMatch =
      !activeFilters.partNumber ||
      activeFilters.partNumber.includes(item.partNumber);
    const descriptionMatch =
      !activeFilters.partDescription ||
      activeFilters.partDescription.includes(item.partDescription);

    const searchMatch = Object.values(item).some((val) =>
      val?.toString().toLowerCase().includes(searchText.toLowerCase())
    );

    return productNameMatch && partNumberMatch && descriptionMatch && searchMatch;
  });

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    const reorderedColumns = Array.from(columns);
    const [removed] = reorderedColumns.splice(result.source.index, 1);
    reorderedColumns.splice(result.destination.index, 0, removed);
    setColumns(reorderedColumns);
  };

  const toggleColumn = (key: string) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };
  const visibleColumns = columns.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
  }));

  const handleResetAll = () => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  const handleCancel = () => {
    setSelectedSpare(null);
    setIsModalVisible(false);
  };

  const handleOk = async () => {
    try {
      const payload = {
        data: {
          id: selectedSpare?.id,
          isDeleted: 1,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditSpare, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
      setSelectedSpare(null);
      setIsModalVisible(false);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Return JSX structure for User Component
  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardOutlined />}
        title={`${filterParams?.totalRows} Total Config Spares`}
        size="large"
      >
        <Space>
          {Utils.isUserHasAccess(
            context.user?.privilegeList,
            ePrivileges.ADDCONFIGSPARE
          ) ? (
            <>
              <CustomDrawer
                size="default"
                trigger={
                  <Button disabled={isLoading} type="primary">
                    Add Spare Product
                  </Button>
                }
                component={SpareProductForm} // Passing Service Partner Form component
                componentProps={{
                  onRefreshList: () => {
                    fetchData();
                  },
                }}
                title="Product Spare Form"
              />

              <CustomDrawer
                size="default"
                trigger={
                  <Button disabled={isLoading} type="primary">
                    Add Spares
                  </Button>
                }
                component={ConfigSpareForm} // Passing Service Partner Form component
                componentProps={{
                  id: 0, // Pass necessary props for ServicePartnerForm
                  onRefreshList: () => {
                    fetchData();
                  },
                }}
                title="Service Partner Form"
              />
            </>
          ) : null}
          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`config_spares_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>{" "}
          {/* <FontAwesomeIcon
          icon={faFilter}
          className="listFilterIcons"
        ></FontAwesomeIcon> */}
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={columns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={() => setColumns(initialColumns)}
          onSave={() => setDrawerVisible(false)}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>

      {/* Modal to ask the confirmation before proceeding with delete operation */}
      <CustomModal
        title="Confirm"
        isOpen={isModalVisible} // Ensure selectedUserId is valid
        okText="Yes"
        cusHeader={true}
        cancelText="No"
        onOk={async () => await handleOk()}
        onCancel={() => handleCancel()}
      >
        <p>
          You are about to delete{" "}
          <b>
            {selectedSpare?.productName} (#{selectedSpare?.partNumber})
          </b>
        </p>
      </CustomModal>
    </div>
  );
}
